import { memo, useSyncExternalStore } from 'react';
import { T } from '../../../translation/src';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { PropertiesSection, Property } from './PropertiesSection';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType } from '../../Properties/types';
import { getOpeningSketchLengthProperty, getOpeningSketchHeightProperty } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';
import { customOpeningTypesVersion, onCustomOpeningTypesChanged } from '../../../potree/sketchObjects/custom/openings/customOpeningRegistry';

interface OpeningSketchPropertiesProps {
  projectId: string;
  identifier: string;
}

/**
 * Right-panel properties for an OPENING_SKETCH (door/window on a wall-sketch
 * edge). All editable params (width, ground offset, height, inset, kind, door
 * type, rollable) are registered `openingsketch_*` properties rendered by the
 * auto-generated {@link PropertyPanel}; the params driven by on-canvas handles
 * instead (offset, hinge, side) are hidden there. A small read-out section
 * surfaces the derived width/height.
 */
const OpeningSketchProperties_: React.FC2<OpeningSketchPropertiesProps> = ({ projectId, identifier }) => {
  const { formatNumber } = useFormatNumber();
  const width = usePropertyValue(getOpeningSketchLengthProperty(identifier));
  const height = usePropertyValue(getOpeningSketchHeightProperty(identifier));
  const openingTypesVersion = useSyncExternalStore(onCustomOpeningTypesChanged, customOpeningTypesVersion);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      {/* Empty leading child so `divide-y` draws a line ABOVE the first section,
          matching the divider it renders between the other sections. */}
      <div />
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={<T _str="width" swc />}>{formatNumber(width ?? 0, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
        <Property title={<T _str="height" swc />}>{formatNumber(Math.max(0, height ?? 0), { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      {/* Remounted when the organisation's doors / windows change: the type dropdowns and their param rows are re-registered then. */}
      <PropertyPanel key={openingTypesVersion} projectId={projectId} annotationTypes={[AnnotationType.OPENING_SKETCH]} objectIdentifiers={[identifier]} />
    </div>
  );
};

export const OpeningSketchProperties = memo(OpeningSketchProperties_);
